import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from '../components/shared/Modal';
import CreateSerialForm from '../components/serials/CreateSerialForm';
import EmptyState from '../components/shared/EmptyState/EmptyState';
import PageHeader from '../components/shared/PageHeader';
import StatCard from '../components/shared/StatCard';
import MilestoneList from '../components/shared/MilestoneList';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import { useSerials, useTasks, useCharacters, usePages, useProjects } from '../hooks';
import { useAutoCreateWeeklyTasks } from '../hooks/useAutoTasks';

const DashboardPage: React.FC = () => {
  useAutoCreateWeeklyTasks();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'tasks' | 'pages'>('tasks');
  const navigate = useNavigate();

  const {
    data: serials = [],
    isLoading: serialsLoading,
    isError: serialsError
  } = useSerials();
  const {
    data: userTasks = [],
    isLoading: tasksLoading,
    error: tasksError
  } = useTasks();
  const { data: characters = [], isLoading: charsLoading } = useCharacters();
  const { data: allPages = [], isLoading: pagesLoading } = usePages();
  const { data: allProjects = [] } = useProjects();

  const handleSerialCreated = () => {
    setIsModalOpen(false);
  };

  // Compute quick stats — only count non-done tasks
  const openTasks = userTasks
    .filter((t: any) => t.status !== 'done')
    .slice(0, 10);
  const pendingTasks = userTasks.filter(
    (t: any) => t.status === 'pending'
  ).length;
  const openTaskCount = userTasks.filter(
    (t: any) => t.status !== 'done'
  ).length;
  const upcomingDeadlines = userTasks
    .filter((t: any) => t.dueAt && new Date(t.dueAt) >= new Date())
    .sort(
      (a: any, b: any) =>
        new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    )
    .slice(0, 3);

  // Recent pages — with project→serial lookup for navigation
  const projectSerialMap = useMemo(() => {
    const map = new Map();
    for (const p of (allProjects as any[])) {
      if (p.id && p.serialId) map.set(p.id, p.serialId);
    }
    return map;
  }, [allProjects]);
  const recentPages = useMemo(
    () =>
      [...allPages]
        .sort(
          (a: any, b: any) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
        .slice(0, 5),
    [allPages]
  );

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <PageHeader
        label="Dashboard"
        title="Your Shows"
        description="A high-level view of your story productions and activity"
        actions={[
          {
            label: 'Create New Show',
            onClick: () => setIsModalOpen(true),
            icon: 'add',
            variant: 'primary'
          }
        ]}
      />

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon="theaters" value={serials.length} label="Shows" onClick={() => navigate('/home/serials')} />
        <StatCard icon="people" value={characters.length} label="Characters" onClick={() => navigate('/home/serials')} valueColor="text-tertiary" />
        <StatCard icon="checklist" value={openTaskCount} label="Open Tasks" onClick={() => navigate('/home/timeline')} valueColor="text-tertiary" />
        <StatCard icon="analytics" value="View All" label="Analytics" onClick={() => navigate('/home/analytics')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Shows Grid */}
        <div className="lg:col-span-8 space-y-5">
          <div className="flex items-center gap-3">
            <span className="material-icons text-on-surface-variant">
              theaters
            </span>
            <h2 className="text-lg font-display font-bold text-on-surface tracking-tight">
              Shows
            </h2>
            {!serialsLoading && (
              <div className="badge badge-neutral ml-auto">
                {serials.length}
              </div>
            )}
            <button
              onClick={() => navigate('/home/serials')}
              className="btn btn-ghost btn-sm text-on-surface-variant ml-2"
            >
              View All →
            </button>
          </div>

          {serialsLoading ? (
            <LoadingSkeleton count={4} columns={2} />
          ) : serialsError ? (
            <div className="alert alert-error">
              <span className="material-icons">error_outline</span>
              <span>Error fetching shows. Please try again later.</span>
            </div>
          ) : serials.length === 0 ? (
            <EmptyState
              icon="theaters"
              title="No shows yet"
              description="A show is your top-level story universe — like a TV series or comic book run. Create your first show to start building your production pipeline."
              illustration="series"
              actions={[
                {
                  label: 'Create New Show',
                  onClick: () => setIsModalOpen(true),
                  icon: 'add',
                  variant: 'primary'
                }
              ]}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {serials.map((serial, idx) => (
                  <motion.div
                    key={serial.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    onClick={() => navigate(`/home/serials/${serial.id}`)}
                    className="group cursor-pointer card hover:bg-surface-container transition-all duration-150"
                  >
                    <div className="p-5 space-y-3">
                      <h3 className="text-on-surface font-bold text-lg font-display tracking-tight">
                        {serial.key}
                      </h3>
                      <p className="text-on-surface-variant/50 text-sm line-clamp-2 min-h-[2.5rem]">
                        {serial.description ||
                          'Professional show for story production and asset management.'}
                      </p>
                      <div className="flex items-center gap-4 pt-3 border-t border-outline-variant/20">
                        <span className="flex items-center gap-1.5 text-xs font-mono text-on-surface-variant/40">
                          <span className="material-icons text-xs">
                            auto_stories
                          </span>
                          Projects Hub
                        </span>
                        <span className="flex items-center gap-1 text-xs font-mono text-tertiary ml-auto group-hover:translate-x-1 transition-transform">
                          View Details
                          <span className="material-icons text-xs">
                            arrow_forward
                          </span>
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Sidebar / Tasks + Pages + Upcoming */}
        <div className="lg:col-span-4 space-y-5">
          <div className="card">
            <div className="border-b border-outline-variant/20">
              <div className="flex items-center gap-1 px-5 pt-4">
                <button
                  onClick={() => setSidebarTab('tasks')}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    sidebarTab === 'tasks'
                      ? 'border-tertiary text-tertiary'
                      : 'border-transparent text-on-surface-variant/50 hover:text-on-surface-variant'
                  }`}
                >
                  <span className="material-icons text-sm">
                    assignment_turned_in
                  </span>
                  Tasks
                  {!tasksLoading && userTasks.length > 0 && (
                    <span className="badge badge-neutral text-[0.55rem] ml-1">
                      {userTasks.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setSidebarTab('pages')}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    sidebarTab === 'pages'
                      ? 'border-tertiary text-tertiary'
                      : 'border-transparent text-on-surface-variant/50 hover:text-on-surface-variant'
                  }`}
                >
                  <span className="material-icons text-sm">description</span>
                  Recent Pages
                </button>
              </div>
            </div>

            <div className="p-4">
              {sidebarTab === 'tasks' && (
                <div>
                  {tasksLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-10 bg-surface-container animate-pulse border border-outline-variant/5"
                        />
                      ))}
                    </div>
                  ) : openTasks.length === 0 ? (
                    <div className="text-center py-6">
                      <span className="material-icons-outlined text-3xl text-on-surface-variant/15 block mb-2">
                        checklist
                      </span>
                      <p className="text-xs text-on-surface-variant/30">
                        All tasks completed! 🎉
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {openTasks.map((task: any) => (
                        <div
                          key={task.id}
                          className="flex items-center gap-3 py-2 group cursor-pointer hover:bg-surface-container/50 transition-colors"
                          onClick={() => navigate('/home/timeline')}
                        >
                          <div
                            className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center transition-colors ${task.status === 'in_progress' ? 'bg-tertiary/20 border-tertiary/50' : 'border-outline-variant/40'}`}
                          >
                            {task.status === 'in_progress' && (
                              <span className="material-icons text-[10px] text-tertiary">
                                radio_button_checked
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate text-on-surface">
                              {task.title}
                            </p>
                            {task.dueAt && (
                              <p className="text-[10px] text-on-surface-variant/30 font-mono">
                                Due{' '}
                                {task.dueAt instanceof Date
                                  ? task.dueAt.toLocaleDateString()
                                  : new Date(task.dueAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <span
                            className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 ${task.status === 'in_progress' ? 'bg-tertiary/10 text-tertiary' : 'bg-surface-container-high text-on-surface-variant/40'}`}
                          >
                            {task.status === 'in_progress'
                              ? 'WIP'
                              : task.status}
                          </span>
                        </div>
                      ))}
                      {openTaskCount > 10 && (
                        <div className="pt-2">
                          <button
                            onClick={() => navigate('/home/timeline')}
                            className="text-xs text-tertiary hover:text-tertiary/80 transition-colors flex items-center gap-1"
                          >
                            View all {openTaskCount} tasks
                            <span className="material-icons text-xs">arrow_forward</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {sidebarTab === 'pages' && (
                <div>
                  {pagesLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-10 bg-surface-container animate-pulse border border-outline-variant/5"
                        />
                      ))}
                    </div>
                  ) : allPages.length === 0 ? (
                    <div className="text-center py-6">
                      <span className="material-icons-outlined text-3xl text-on-surface-variant/15 block mb-2">
                        description
                      </span>
                      <p className="text-xs text-on-surface-variant/30">
                        No pages yet
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-outline-variant/10">
                      {recentPages.map((page: any) => (
                        <div
                          key={page.id}
                          className="flex items-center gap-3 py-2.5 hover:bg-surface-container transition-colors cursor-pointer"
                          onClick={() => {
                            const serialId = page.projectId ? projectSerialMap.get(page.projectId) : undefined;
                            if (page.projectId && serialId) {
                              navigate(
                                `/home/serials/${serialId}/projects/${page.projectId}/pages/${page.id}/edit`
                              );
                            }
                          }}
                        >
                          <div className="w-7 h-7 flex items-center justify-center border border-outline-variant/30 flex-shrink-0">
                            <span className="material-icons-outlined text-on-surface-variant/40 text-xs">
                              description
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-on-surface truncate">
                              {page.title || `Page ${page.pageNumber}`}
                            </p>
                            <p className="text-[10px] text-on-surface-variant/30 font-mono">
                              Updated{' '}
                              {page.updatedAt instanceof Date
                                ? page.updatedAt.toLocaleDateString()
                                : new Date(page.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <span
                            className={`text-[9px] font-mono px-1.5 py-0.5 ${page.status === 'done' ? 'bg-success/10 text-success' : page.status === 'draft' ? 'bg-outline-variant/10 text-on-surface-variant/40' : 'bg-tertiary/10 text-tertiary'}`}
                          >
                            {page.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Deadlines */}
          {upcomingDeadlines.length > 0 && (
            <div className="card">
              <div className="p-5 border-b border-outline-variant/20">
                <div className="flex items-center gap-3">
                  <span className="material-icons text-tertiary text-lg">
                    event
                  </span>
                  <h3 className="text-lg font-display font-bold text-on-surface tracking-tight">
                    Upcoming Deadlines
                  </h3>
                </div>
              </div>
              <div className="p-5">
                <MilestoneList
                  milestones={upcomingDeadlines.map((t: any) => ({
                    id: t.id,
                    title: t.title,
                    dueAt: t.dueAt,
                    status: t.status,
                  }))}
                  onViewAll={() => navigate('/home/timeline')}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New Show"
      >
        <div className="p-2">
          <CreateSerialForm onCreated={handleSerialCreated} />
        </div>
      </Modal>
    </div>
  );
};

export default DashboardPage;
